import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  ElementRef
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { EquineService } from '../equine.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { requireEquineQuoteId, resolveEquinePreviousPolicyId, scrollToFirstInvalidControl } from '../equine-utils';

const PROPOSED_HORSE_CLAIM_TYPES = [
  "Vet's Fees",
  "Loss of Use",
  "Third Party Liability",
  "Saddlery & Tack",
  "Personal Accident",
  "Other"
];

const OTHER_HORSES_CLAIM_TYPES = [
  "Mortality",
  "Vet's Fees",
  "Loss of Use",
  "Third Party Liability",
  "Saddlery & Tack",
  "Personal Accident",
  "Other"
];

const CLAIM_STATUSES = [
  { value: 0, label: "Accepted (complete)" },
  { value: 1, label: "Accepted (ongoing)" },
  { value: 2, label: "Declined" }
];

@Component({
  selector: 'app-equine-claims',
  templateUrl: './equine-claims.component.html',
  styleUrls: ['./equine-claims.component.css', '../../quote.component.css']
})
export class EquineClaimsComponent implements OnInit, OnChanges {
  @Input() staticData: any;
  @Input() modelObject: any = {};
  @Output() modelObjectUpdated = new EventEmitter<any>();
  @Output() GoToNextTab = new EventEmitter<void>();
  @Output() GoToPrevTab = new EventEmitter<void>();

  formFields: FormGroup;
  submitted = false;
  saving = false;

  readonly proposedHorseClaimTypes = PROPOSED_HORSE_CLAIM_TYPES;
  readonly otherHorsesClaimTypes = OTHER_HORSES_CLAIM_TYPES;
  readonly claimStatuses = CLAIM_STATUSES;
  /** Alias for template (opt.value / opt.label). */
  readonly claimStatusOptions = CLAIM_STATUSES;
  /** For dropdowns: { id, name } — id is the display name (API may accept or we map later). */
  proposedClaimTypes: { id: string; name: string }[] = [];
  otherClaimTypes: { id: string; name: string }[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private equineService: EquineService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.setupClaimsSectionValidators();
    // Load claim types from API so we send UUID (backend stores UUID). Fallback to names if API fails.
    forkJoin({
      proposed: this.equineService.getClaimTypes(1),
      other: this.equineService.getClaimTypes(2)
    }).subscribe({
      next: (res) => {
        const proposed = res.proposed?.result;
        const other = res.other?.result;
        // process proposed claim types
        if (Array.isArray(proposed) && proposed.length > 0) {
          this.proposedClaimTypes = proposed.map((t: any) => ({ id: t.id, name: t.name || t.id }));
        } else {
          this.proposedClaimTypes = PROPOSED_HORSE_CLAIM_TYPES.map(name => ({ id: name, name }));
        }
        // process other claim types
        if (Array.isArray(other) && other.length > 0) {
          this.otherClaimTypes = other.map((t: any) => ({ id: t.id, name: t.name || t.id }));
        } else {
          this.otherClaimTypes = OTHER_HORSES_CLAIM_TYPES.map(name => ({ id: name, name }));
        }
        this.updateClaimGroupsValidity();
        this.cdr.detectChanges();
      },
      error: () => {
        this.proposedClaimTypes = PROPOSED_HORSE_CLAIM_TYPES.map(name => ({ id: name, name }));
        this.otherClaimTypes = OTHER_HORSES_CLAIM_TYPES.map(name => ({ id: name, name }));
        this.cdr.detectChanges();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.modelObject && this.modelObject && Object.keys(this.modelObject).length > 0) {
      this.patchFormValues();
    }
  }

  get stableName(): string {
    const name = this.modelObject?.horse_details?.stable_name || this.modelObject?.horse_details?.name;
    return name ? String(name) : '[STABLE NAME]';
  }

  get proposedClaims(): FormArray {
    return this.formFields.get('proposed_horse_claims') as FormArray;
  }

  get otherHorsesClaims(): FormArray {
    return this.formFields.get('other_horses_claims') as FormArray;
  }

  /** Whether the selected claim type is "Other" for the given row (used in template to show other_claim field). */
  isOtherClaimTypeSelected(index: number, section: 'proposed' | 'other'): boolean {
    const arr = section === 'proposed' ? this.proposedClaims : this.otherHorsesClaims;
    const types = section === 'proposed' ? this.proposedClaimTypes : this.otherClaimTypes;
    const value = arr.at(index)?.get('claim_type')?.value;
    if (!value) return false;
    const found = types.find(t => t.id === value);
    return found?.name === 'Other';
  }

  initializeForm(): void {
    this.formFields = this.formBuilder.group({
      proposed_horse_has_claims: ['', Validators.required],
      proposed_horse_claims: this.formBuilder.array([this.createClaimGroup('proposed')]),
      other_horses_has_claims: ['', Validators.required],
      other_horses_claims: this.formBuilder.array([this.createClaimGroup('other')])
    });
  }

  createClaimGroup(section: 'proposed' | 'other'): FormGroup {
    const group = this.formBuilder.group({
      claim_date: ['', [Validators.required]],
      claim_type: ['', [Validators.required]],
      claim_cause: ['', [Validators.required]],
      claim_status: ['', [Validators.required]],
      settlement_amount: ['', [Validators.required]],
      other_claim: [''],
      is_any_other_horse_claim: [0]
    }, { validators: this.claimGroupValidator });
    return group;
  }

  /** Group validator: require other_claim when selected claim type is "Other" (by id or name). */
  private claimGroupValidator = (group: AbstractControl): { [key: string]: any } | null => {
    const g = group as FormGroup;
    const claimType = g.get('claim_type')?.value;
    const otherClaim = (g.get('other_claim')?.value || '').trim();
    const isOther =
      claimType === 'Other' ||
      (typeof claimType === 'string' && claimType.toLowerCase() === 'other') ||
      this.proposedClaimTypes.some((t: any) => t.id === claimType && t.name === 'Other') ||
      this.otherClaimTypes.some((t: any) => t.id === claimType && t.name === 'Other');
    if (isOther && !otherClaim) {
      g.get('other_claim')?.setErrors({ required: true });
      return { otherClaimRequired: true };
    }
    g.get('other_claim')?.setErrors(null);
    return null;
  };

  /** Run validators on all claim rows (e.g. after claim types load so "Other" is detected). */
  private updateClaimGroupsValidity(): void {
    this.proposedClaims?.controls?.forEach((c: AbstractControl) => (c as FormGroup).updateValueAndValidity());
    this.otherHorsesClaims?.controls?.forEach((c: AbstractControl) => (c as FormGroup).updateValueAndValidity());
  }

  /** When "No" is selected for a claims section, clear required validators on that section's rows so the form can be valid. */
  private setupClaimsSectionValidators(): void {
    const proposedHas = this.formFields.get('proposed_horse_has_claims');
    const otherHas = this.formFields.get('other_horses_has_claims');
    const updateAll = () => {
      this.updateClaimArrayValidators(this.proposedClaims, proposedHas?.value === 1 || proposedHas?.value === '1');
      this.updateClaimArrayValidators(this.otherHorsesClaims, otherHas?.value === 1 || otherHas?.value === '1');
    };
    proposedHas?.valueChanges?.subscribe?.(() => updateAll());
    otherHas?.valueChanges?.subscribe?.(() => updateAll());
  }

  private updateClaimArrayValidators(arr: FormArray, required: boolean): void {
    if (!arr?.controls) return;
    arr.controls.forEach((c: AbstractControl) => {
      const g = c as FormGroup;
      ['claim_date', 'claim_type', 'claim_cause', 'claim_status', 'settlement_amount'].forEach(key => {
        const control = g.get(key);
        if (control) {
          control.setValidators(required ? [Validators.required] : []);
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
    });
    arr.updateValueAndValidity({ emitEvent: false });
  }

  addProposedClaim(): void {
    this.proposedClaims.push(this.createClaimGroup('proposed'));
    this.cdr.detectChanges();
  }

  removeProposedClaim(index: number): void {
    if (this.proposedClaims.length > 1) {
      this.proposedClaims.removeAt(index);
    }
  }

  addOtherHorsesClaim(): void {
    this.otherHorsesClaims.push(this.createClaimGroup('other'));
    this.cdr.detectChanges();
  }

  removeOtherHorsesClaim(index: number): void {
    if (this.otherHorsesClaims.length > 1) {
      this.otherHorsesClaims.removeAt(index);
    }
  }

  patchFormValues(): void {
    const history = this.modelObject.claims_history || [];
    const legacy = this.modelObject.claims || {};
    const proposed: any[] = [];
    const other: any[] = [];

    if (Array.isArray(history) && history.length > 0) {
      history.forEach((c: any) => {
        const row = {
          claim_date: this.formatClaimDateForDisplay(c.claim_date),
          claim_type: c.claim_type || '',
          claim_cause: c.claim_cause != null ? c.claim_cause : '',
          claim_status: c.claim_status != null ? Number(c.claim_status) : 0,
          settlement_amount: c.settlement_amount != null ? c.settlement_amount : '',
          is_any_other_horse_claim: c.is_any_other_horse_claim === 1 || c.is_any_other_horse_claim === '1' ? 1 : 0,
          other_claim: c.other_claim || ''
        };
        if (c.horse_type === 2 || c.horse_type === '2') other.push(row);
        else proposed.push(row);
      });
    } else {
      const lp = legacy.proposed_horse_claims || [];
      const lo = legacy.other_horses_claims || [];

      const isEditing = !!this.route.snapshot.params.id;
      if(lp.length === 0 && isEditing) { 
        this.formFields.get('proposed_horse_has_claims').setValue(0);
      }

      if(lo.length === 0 && isEditing) { 
        this.formFields.get('other_horses_has_claims').setValue(0);
      }

      lp.forEach((c: any) => proposed.push({
        claim_date: c.claim_date || '',
        claim_type: c.claim_type || '',
        claim_cause: c.cause != null ? c.cause : (c.claim_cause != null ? c.claim_cause : ''),
        claim_status: c.claim_status != null ? c.claim_status : (c.status !== '' && c.status !== undefined ? this.statusLabelToValue(c.status) : 0),
        settlement_amount: c.settlement_amount != null ? c.settlement_amount : '',
        is_any_other_horse_claim: c.is_any_other_horse_claim !== undefined ? c.is_any_other_horse_claim : (c.has_other === 1 || c.has_other === '1' ? 1 : 0),
        other_claim: c.other_claim || ''
      }));
      lo.forEach((c: any) => other.push({
        claim_date: c.claim_date || '',
        claim_type: c.claim_type || '',
        claim_cause: c.cause != null ? c.cause : (c.claim_cause != null ? c.claim_cause : ''),
        claim_status: c.claim_status != null ? c.claim_status : (c.status !== '' && c.status !== undefined ? this.statusLabelToValue(c.status) : 0),
        settlement_amount: c.settlement_amount != null ? c.settlement_amount : '',
        is_any_other_horse_claim: c.is_any_other_horse_claim !== undefined ? c.is_any_other_horse_claim : (c.has_other === 1 || c.has_other === '1' ? 1 : 0),
        other_claim: c.other_claim || ''
      }));
    }

    if (legacy.proposed_horse_has_claims !== undefined && legacy.proposed_horse_has_claims !== null && legacy.proposed_horse_has_claims !== '') {
      this.formFields.get('proposed_horse_has_claims').setValue(
        legacy.proposed_horse_has_claims === 1 || legacy.proposed_horse_has_claims === '1' || legacy.proposed_horse_has_claims === true ? 1 : 0
      );
    } else if (proposed.length > 0) {
      this.formFields.get('proposed_horse_has_claims').setValue(1);
    } else if (Array.isArray(history) && history.length > 0) {
      this.formFields.get('proposed_horse_has_claims').setValue(0);
    }
    this.proposedClaims.clear();
    if (proposed.length > 0) {
      proposed.forEach((c: any) => this.proposedClaims.push(this.patchClaimGroup('proposed', c)));
    } else {
      this.proposedClaims.push(this.createClaimGroup('proposed'));
    }

    if (legacy.other_horses_has_claims !== undefined && legacy.other_horses_has_claims !== null && legacy.other_horses_has_claims !== '') {
      this.formFields.get('other_horses_has_claims').setValue(
        legacy.other_horses_has_claims === 1 || legacy.other_horses_has_claims === '1' || legacy.other_horses_has_claims === true ? 1 : 0
      );
    } else if (other.length > 0) {
      this.formFields.get('other_horses_has_claims').setValue(1);
    } else if (Array.isArray(history) && history.length > 0) {
      this.formFields.get('other_horses_has_claims').setValue(0);
    }
    
    this.otherHorsesClaims.clear();
    if (other.length > 0) {
      other.forEach((c: any) => this.otherHorsesClaims.push(this.patchClaimGroup('other', c)));
    } else {
      this.otherHorsesClaims.push(this.createClaimGroup('other'));
    }
    this.updateClaimGroupsValidity();
    this.updateClaimArrayValidators(this.proposedClaims, this.formFields.get('proposed_horse_has_claims')?.value === 1 || this.formFields.get('proposed_horse_has_claims')?.value === '1');
    this.updateClaimArrayValidators(this.otherHorsesClaims, this.formFields.get('other_horses_has_claims')?.value === 1 || this.formFields.get('other_horses_has_claims')?.value === '1');
    this.cdr.detectChanges();
  }

  private formatClaimDateForDisplay(d: any): string {
    if (!d) return '';
    const s = String(d).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, day] = s.split('-');
      return `${day}-${m}-${y}`;
    }
    return s;
  }

  /** Parse claim date string (DD-MM-YYYY, YYYY-MM-DD, etc.) to Date for mat-datepicker. */
  private parseClaimDateForPicker(d: any): Date | null {
    if (!d) return null;
    const s = String(d).trim();
    const ddmmyy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
    if (ddmmyy) {
      const date = new Date(+ddmmyy[3], +ddmmyy[2] - 1, +ddmmyy[1]);
      return isNaN(date.getTime()) ? null : date;
    }
    const ymd = /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/.exec(s);
    if (ymd) {
      const date = new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
      return isNaN(date.getTime()) ? null : date;
    }
    const parsed = new Date(s);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private statusLabelToValue(label: string): number {
    const opt = CLAIM_STATUSES.find(o => o.label === label);
    return opt ? opt.value : 0;
  }

  private patchClaimGroup(section: 'proposed' | 'other', data: any): FormGroup {
    const g = this.createClaimGroup(section);
    if (data.claim_date != null && data.claim_date !== '') {
      const parsed = this.parseClaimDateForPicker(data.claim_date);
      g.get('claim_date').setValue(parsed != null ? parsed : data.claim_date);
    }
    if (data.claim_type != null) g.get('claim_type').setValue(data.claim_type);
    if (data.claim_cause !== undefined) g.get('claim_cause').setValue(data.claim_cause);
    if (data.claim_status !== undefined) g.get('claim_status').setValue(Number(data.claim_status));
    if (data.settlement_amount !== undefined && data.settlement_amount !== null) g.get('settlement_amount').setValue(data.settlement_amount);
    if (data.is_any_other_horse_claim !== undefined) g.get('is_any_other_horse_claim').setValue(data.is_any_other_horse_claim === 1 || data.is_any_other_horse_claim === '1' ? 1 : 0);
    if (data.other_claim !== undefined) g.get('other_claim').setValue(data.other_claim || '');
    return g;
  }

  get f(): { [key: string]: AbstractControl } {
    return this.formFields.controls;
  }

  /** Format claim date for API: DD-MM-YYYY. Accepts Date, DD-MM-YYYY, MM/YYYY, YYYY-MM-DD, etc. */
  private formatClaimDateForApi(val: any): string {
    if (val == null || val === '') return '';
    if (val instanceof Date && !isNaN(val.getTime())) {
      const d = val.getDate(), m = val.getMonth() + 1, y = val.getFullYear();
      return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
    }
    const s = String(val).trim();
    const ddmmyy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(s);
    if (ddmmyy) return `${ddmmyy[1].padStart(2, '0')}-${ddmmyy[2].padStart(2, '0')}-${ddmmyy[3]}`;
    const mmyy = /^(\d{1,2})[\/\-](\d{4})$/.exec(s);
    if (mmyy) return `01-${mmyy[1].padStart(2, '0')}-${mmyy[2]}`;
    const ymd = /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/.exec(s);
    if (ymd) return `${ymd[3].padStart(2, '0')}-${ymd[2].padStart(2, '0')}-${ymd[1]}`;
    return s;
  }

  /** Build flat claims array for save quote API. */
  private buildClaimsPayload(formValue: any): any[] {
    const claims: any[] = [];
    const proposedHas = formValue.proposed_horse_has_claims === 1 || formValue.proposed_horse_has_claims === '1';
    const otherHas = formValue.other_horses_has_claims === 1 || formValue.other_horses_has_claims === '1';

    if (proposedHas && Array.isArray(formValue.proposed_horse_claims)) {
      formValue.proposed_horse_claims.forEach((c: any) => {
        if (!c.claim_type && !c.claim_date && c.claim_cause === '' && (c.settlement_amount === '' || c.settlement_amount == null)) return;
        claims.push({
          has_claim: 1,
          claim_date: this.formatClaimDateForApi(c.claim_date) || '01-01-2020',
          claim_type: c.claim_type || '',
          claim_cause: c.claim_cause != null ? String(c.claim_cause) : '',
          claim_status: c.claim_status != null ? Number(c.claim_status) : 0,
          settlement_amount: c.settlement_amount !== '' && c.settlement_amount != null ? Number(c.settlement_amount) : 0,
          is_any_other_horse_claim: c.is_any_other_horse_claim === 1 || c.is_any_other_horse_claim === '1' ? 1 : 0,
          other_claim: c.other_claim && String(c.other_claim).trim() ? String(c.other_claim).trim() : null,
          horse_type: 1
        });
      });
    }
    if (otherHas && Array.isArray(formValue.other_horses_claims)) {
      formValue.other_horses_claims.forEach((c: any) => {
        if (!c.claim_type && !c.claim_date && c.claim_cause === '' && (c.settlement_amount === '' || c.settlement_amount == null)) return;
        claims.push({
          has_claim: 1,
          claim_date: this.formatClaimDateForApi(c.claim_date) || '01-01-2020',
          claim_type: c.claim_type || '',
          claim_cause: c.claim_cause != null ? String(c.claim_cause) : '',
          claim_status: c.claim_status != null ? Number(c.claim_status) : 0,
          settlement_amount: c.settlement_amount !== '' && c.settlement_amount != null ? Number(c.settlement_amount) : 0,
          is_any_other_horse_claim: c.is_any_other_horse_claim === 1 || c.is_any_other_horse_claim === '1' ? 1 : 0,
          other_claim: c.other_claim && String(c.other_claim).trim() ? String(c.other_claim).trim() : null,
          horse_type: 2
        });
      });
    }
    return claims;
  }

  /** Save and optionally go to next tab. goToNext: true = Save & Continue, false = Save Indicative Quote only. */
  onSubmit(goToNext = true): void {
    this.submitted = true;
    if (this.formFields.invalid) {
      this.formFields.markAllAsTouched();
      scrollToFirstInvalidControl(this.formFields, this.el.nativeElement);
      return;
    }

    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId || !this.modelObject.quote_meta_id) {
      this._snackBar.open('No quote ID found. Please save Quote Details first.', 'Close', { duration: 3000 });
      return;
    }

    const formValue = this.formFields.getRawValue();
    const claimsArray = this.buildClaimsPayload(formValue);

    const data: any = {
      ...this.modelObject,
      previous_policy_id: resolveEquinePreviousPolicyId(this.modelObject),
      id: quoteId,
      transaction_type: this.modelObject.type ?? this.modelObject.transaction_type ?? 1,
      has_form_changed: this.formFields.dirty,
      claims: claimsArray
    };

    const prevPolicyId = this.modelObject.previous_policy_id;
    const txnType = this.modelObject.type || this.modelObject.transaction_type;
    const origTxType = this.modelObject.original_transaction_type;
    this.saving = true;
    this.equineService.saveQuote(data).subscribe(
      (response: any) => {
        this.saving = false;
        if (response.success) {
          this._snackBar.open('Claims details saved successfully', 'Close', { duration: 3000 });
          const updated = response.result;
          if (prevPolicyId) updated.previous_policy_id = prevPolicyId;
          if (txnType) updated.type = txnType;
          if (origTxType) updated.original_transaction_type = origTxType;
          this.submitted = false;
          this.modelObjectUpdated.emit(updated);
          if (goToNext) {
            this.GoToNextTab.emit();
          }
        }
      },
      () => {
        this.saving = false;
        this.submitted = false;
        this._snackBar.open('Error saving claims details', 'Close', { duration: 3000 });
      }
    );
  }

  public onClaimDateChange(event: any, index: number, section: 'proposed' | 'other'): void {
    const arr = section === 'proposed' ? this.proposedClaims : this.otherHorsesClaims;
    arr.at(index).get('claim_date').setValue(event.value);
  }
}
